import React, { FC, useState } from 'react'

import { DaxUsagesResponse, Table } from 'tabs/data/api/generated/models'
import TabTile from 'tabs/data/components/TabTile'
import { Highlighted } from 'tabs/data/components/Highlighted'
import MetricsForm from './MetricsForm'
import { NewDatasetMetrics } from './NewDatasetMetrics'
import { deleteTableMetric, UpdateDatasetMetricParams } from 'tabs/data/api/metrics'
import { useDatasetMetrics } from 'tabs/data/pages/descriptions/hooks/useDatasetMetrics'
import { alertError, alertSuccess } from 'api'
import { RelatedResourcesModal } from 'tabs/data/components/RelatedResourcesModal'
import { cloneDeep } from 'lodash'

type DatasetTablesProps = {
  highlight: string
  datasetId: string
  tableId: string
  metrics: Table['metrics']
  refreshCallback: () => Promise<void>
}

export const DatasetMetrics: FC<DatasetTablesProps> = ({ datasetId, tableId, highlight, metrics, refreshCallback }) => {
  const { updateMetric } = useDatasetMetrics()

  const [warningData, setWarningData] = useState<null | DaxUsagesResponse>(null)
  const [forceUpdateParams, setForceUpdateParams] = useState<{
    metricId?: string
    params?: UpdateDatasetMetricParams
  } | null>(null)
  const [expandedMetrics, setExpandeMetrics] = useState('')

  const handleChangeExpandedMetrics = (metricId: string) => {
    const nextValue = expandedMetrics !== metricId ? metricId : ''
    setExpandeMetrics(nextValue)
  }

  const handleUpdateMetric = async (metricId: string, params: UpdateDatasetMetricParams, forceUpdate = false) => {
    try {
      const response = await updateMetric(metricId, datasetId, tableId, forceUpdate, params)

      if (response) {
        setWarningData(null)
        setForceUpdateParams(null)
        alertSuccess('Metric updated', 3000)
        await refreshCallback()
      }
    } catch (error) {
      switch (error.response.status) {
        case 404:
          throw alertError('The table that was already deleted')
        case 409:
          if (error?.response) {
            const errorData = await error.response.json()

            if (errorData?.usages) {
              setForceUpdateParams({ metricId, params: cloneDeep(params) })
              return setWarningData(errorData)
            }
          }

          throw alertError("Conflict. Can't use the name")
        case 412:
          setWarningData(null)
          throw alertError('Error! Someone already made changes. Please reload page and try again.')
        default:
          setWarningData(null)

          if (error?.response) {
            const errorData = await error.response.json()

            if (errorData?.errorMessage) throw alertError(errorData?.errorMessage)
          }

          throw alertError(error?.message || error || 'Error updating metric')
      }
    }
  }

  const handelForceUpdateMetric = async () => {
    await handleUpdateMetric(forceUpdateParams.metricId, forceUpdateParams.params, true)
    setForceUpdateParams(null)
  }

  const handleMetricDelete = async (metricId: string, force: boolean) => {
    try {
      await deleteTableMetric({ datasetId, tableId, metricId, force })
      alertSuccess('Metric deleted', 3000)
      await refreshCallback()
    } catch (error) {
      if (error.response.status === 404) {
        throw alertError('The table that was already deleted')
      }

      throw error
    }
  }

  return (
    <div
      style={{
        background: 'var(--color-selago-grey)',
        margin: '0 -1rem',
        padding: '0 1rem 1rem',
        borderRadius: '10px',
      }}>
      {metrics?.map((metricsItem, metricsIndex) => (
        <TabTile
          key={metricsItem?.metricId + metricsIndex}
          tab={{
            name: (
              <Highlighted
                text={`${metricsItem.metricName}${!metricsItem.isActive ? ' (Inactive)' : ''}`}
                highlight={highlight}
              />
            ),
            icon: `${metricsIndex + 1}`,
          }}
          expanded={expandedMetrics === metricsItem.metricId}
          onChange={() => handleChangeExpandedMetrics(metricsItem.metricId)}>
          {expandedMetrics === metricsItem.metricId && (
            <MetricsForm
              updatedAt={metricsItem.updatedAt}
              name={metricsItem.metricName}
              description={metricsItem.metricDescription}
              type={metricsItem.metricType}
              rules={metricsItem.rules}
              formula={metricsItem.metricFormula}
              synonyms={metricsItem.metricSynonyms}
              isActive={metricsItem.isActive ?? true}
              cancelButtonLabel="Reset"
              onSubmit={async params => {
                handleUpdateMetric(metricsItem.metricId, { ...params, version: metricsItem.metricVersion })
              }}
              updateCallback={refreshCallback}
              onDelete={handleMetricDelete.bind(null, metricsItem.metricId)}
            />
          )}
        </TabTile>
      ))}

      <NewDatasetMetrics
        datasetId={datasetId}
        tableId={tableId}
        hasItems={metrics?.length > 0}
        onSubmit={refreshCallback}
      />

      <RelatedResourcesModal
        open={Boolean(warningData?.usages)}
        data={warningData?.usages || {}}
        submitLabel="Save Anyway"
        message={`"${warningData?.name || 'Name'}" is used in the following places:`}
        onClose={() => setWarningData(null)}
        onSubmit={handelForceUpdateMetric}
      />
    </div>
  )
}
